import clsx from "clsx";

/**
 * @fileoverview Branded Material-UI buttons with semantic color variants.
 *
 * Import via `MaterialUI.Button` and pick a color (e.g. `Primary`, `Danger`).
 * All variants support MUI `variant` values: `contained` (default), `outlined`, `text`.
 *
 * @example
 * // Primary action
 * <MaterialUI.Button.Success onClick={handleSave}>Save</MaterialUI.Button.Success>
 *
 * @example
 * // Secondary / cancel action
 * <MaterialUI.Button.Secondary variant="outlined" onClick={onCancel}>Cancel</MaterialUI.Button.Secondary>
 *
 * @example
 * // Icon-only with tooltip
 * <MaterialUI.Button.Primary icon tooltip="Edit" size="small" onClick={onEdit}>
 *   <EditIcon />
 * </MaterialUI.Button.Primary>
 */

import { Button as MuiButton, Tooltip, withStyles } from "@material-ui/core";

/**
 * @typedef {'contained' | 'outlined' | 'text'} ButtonVariant
 * MUI visual style. `contained` is the default when omitted.
 */

/**
 * @typedef {Object} ButtonPaletteInput
 * @property {string} main - Primary color for outlined/text variants and borders.
 * @property {string} hover - Color on hover (background, border, or text).
 * @property {string} light - Subtle background tint on outlined/text hover.
 * @property {string} [containedBg] - Filled background; defaults to `main`.
 * @property {string} [containedHover] - Filled hover background; defaults to `hover`.
 * @property {string} [textOnMain='#fff'] - Text color on contained buttons.
 * @property {string} [shadow] - Box-shadow color on contained hover.
 */

/**
 * @typedef {Object} ButtonPalette
 * @property {string} main
 * @property {string} hover
 * @property {string} light
 * @property {string} containedBg
 * @property {string} containedHover
 * @property {string} textOnMain
 * @property {string} shadow
 */

/**
 * @typedef {import('@material-ui/core').ButtonProps & {
 *   tooltip?: string,
 *   icon?: boolean,
 *   variant?: ButtonVariant,
 * }} BrandedButtonProps
 * Extends MUI Button props. Set `icon` for square icon-only buttons (50px wide).
 * Wraps the button in a top tooltip when `tooltip` is provided and the button is not disabled.
 */

/**
 * Builds a normalized color palette from partial input.
 *
 * @param {ButtonPaletteInput} colors
 * @returns {ButtonPalette}
 */
const createPalette = ({
	main,
	hover,
	light,
	containedBg,
	containedHover,
	textOnMain = "#fff",
	shadow,
}) => ({
	main,
	hover,
	light,
	containedBg: containedBg ?? main,
	containedHover: containedHover ?? hover,
	textOnMain,
	shadow: shadow ?? "rgba(15, 23, 42, 0.15)",
});

/**
 * Returns a `withStyles` factory that maps a palette to contained, outlined, and text styles.
 *
 * @param {ButtonPalette} palette
 * @returns {() => { root: object, contained: object, outlined: object, text: object }}
 */
const createButtonStyles = (palette) => () => ({
	contained: {
		color: palette.textOnMain,
		backgroundColor: palette.containedBg,
		"&:hover": {
			backgroundColor: palette.containedHover,
			boxShadow: `0 1px 2px ${palette.shadow}`,
		},
	},
	outlined: {
		color: palette.main,
		backgroundColor: "transparent",
		border: `1.5px solid ${palette.main}`,
		boxShadow: "none",
		"&:hover": {
			backgroundColor: palette.light,
			borderColor: palette.hover,
			color: palette.hover,
			boxShadow: "none",
		},
	},
	text: {
		color: palette.main,
		backgroundColor: "transparent",
		boxShadow: "none",
		"&:hover": {
			backgroundColor: palette.light,
			boxShadow: "none",
		},
	},
});

/**
 * HOC that applies shared base styles plus variant-specific palette styles.
 *
 * @param {ReturnType<typeof createButtonStyles>} styles
 * @returns {import('react').ComponentType<BrandedButtonProps>}
 */
const ButtonWrapper = (styles) =>
	withStyles((theme) => {
		const variants = styles(theme);
		return {
			root: {
				...variants.root,
				borderRadius: "8px",
				textTransform: "none",
				// fontWeight: 600,
				fontFamily: "Inter",
				letterSpacing: "0.01em",
				transition: "background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease, border-color 0.15s ease",
				"&:disabled": { opacity: 0.55, boxShadow: "none" },
				"& .MuiButton-label": {
					display: "inline-flex",
					alignItems: "center",
					gap: "0.25rem",
				},
			},
			contained: variants.contained,
			outlined: variants.outlined,
			text: variants.text,
		};
	})(({ children, tooltip, type = "button", icon, style = {}, variant = "contained", ...props }) => {
		let propStyled = style || {};

		if (icon) {
			propStyled = { ...propStyled, minWidth: 0, width: 50, height: "auto" };
		}

		const button = (
			<MuiButton
				{...props}
				disableElevation
				type={type}
				variant={variant}
				style={{ textTransform: "inherit", ...propStyled }}
			>
				{children}
			</MuiButton>
		);

		if (tooltip && !props.disabled) {
			return (
				<Tooltip title={tooltip} placement="top" arrow>
					{button}
				</Tooltip>
			);
		}

		return button;
	});

/** @type {Record<string, ButtonPalette>} */
export const BUTTON_PALETTES = {
	primary: createPalette({
		main: "#2563eb",
		hover: "#1d4ed8",
		light: "rgba(37, 99, 235, 0.08)",
		shadow: "rgba(37, 99, 235, 0.28)",
	}),
	success: createPalette({
		main: "#059669",
		hover: "#047857",
		light: "rgba(5, 150, 105, 0.08)",
		shadow: "rgba(5, 150, 105, 0.28)",
	}),
	accent: createPalette({
		main: "#e8601c",
		hover: "#c94d12",
		light: "rgba(232, 96, 28, 0.08)",
		shadow: "rgba(232, 96, 28, 0.28)",
	}),
	danger: createPalette({
		main: "#e11d48",
		hover: "#be123c",
		light: "rgba(225, 29, 72, 0.08)",
		shadow: "rgba(225, 29, 72, 0.28)",
	}),
	warning: createPalette({
		main: "#d97706",
		hover: "#b45309",
		light: "rgba(217, 119, 6, 0.08)",
		shadow: "rgba(217, 119, 6, 0.28)",
	}),
	secondary: createPalette({
		main: "#64748b",
		hover: "#475569",
		light: "rgba(100, 116, 139, 0.08)",
		shadow: "rgba(100, 116, 139, 0.28)",
	}),
	dark: createPalette({
		main: "#1e293b",
		hover: "#0f172a",
		light: "rgba(30, 41, 59, 0.08)",
		shadow: "rgba(15, 23, 42, 0.28)",
	}),
	darkblue: createPalette({
		main: "#0b1c30",
		hover: "#071221",
		light: "rgba(11, 28, 48, 0.08)",
		shadow: "rgba(11, 28, 48, 0.32)",
	}),
	darkLight: createPalette({
		main: "#475569",
		hover: "#334155",
		light: "rgba(71, 85, 105, 0.08)",
		shadow: "rgba(71, 85, 105, 0.28)",
	}),
	light: createPalette({
		main: "#64748b",
		hover: "#475569",
		light: "rgba(241, 245, 249, 0.9)",
		textOnMain: "#334155",
		containedBg: "#f1f5f9",
		containedHover: "#e2e8f0",
		shadow: "rgba(15, 23, 42, 0.08)",
	}),
	neutral: createPalette({
		main: "#cbd5e1",
		hover: "#94a3b8",
		light: "rgba(203, 213, 225, 0.35)",
		textOnMain: "#334155",
		containedBg: "#ffffff",
		containedHover: "#f8fafc",
		shadow: "rgba(15, 23, 42, 0.06)",
	}),
};

/**
 * Semantic button components keyed by color role.
 *
 * @type {{
 *   Dark: import('react').ComponentType<BrandedButtonProps>,
 *   Success: import('react').ComponentType<BrandedButtonProps>,
 *   Primary: import('react').ComponentType<BrandedButtonProps>,
 *   Accent: import('react').ComponentType<BrandedButtonProps>,
 *   Danger: import('react').ComponentType<BrandedButtonProps>,
 *   Warning: import('react').ComponentType<BrandedButtonProps>,
 *   Secondary: import('react').ComponentType<BrandedButtonProps>,
 *   Darkblue: import('react').ComponentType<BrandedButtonProps>,
 *   DarkLight: import('react').ComponentType<BrandedButtonProps>,
 *   Light: import('react').ComponentType<BrandedButtonProps>,
 *   Neutral: import('react').ComponentType<BrandedButtonProps>,
 * }}
 */
export default {
	/** Slate dark — status steps, secondary actions on light surfaces. */
	Dark: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.dark)),
	/** Green — confirm, save, approve. */
	Success: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.success)),
	/** Blue — default primary actions. */
	Primary: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.primary)),
	/** Brand orange (#e8601c) — main CTA accent. */
	Accent: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.accent)),
	/** Red — delete, reject, destructive actions. */
	Danger: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.danger)),
	/** Amber — cautionary actions. */
	Warning: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.warning)),
	/** Gray — cancel, dismiss, low-emphasis actions. */
	Secondary: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.secondary)),
	/** Deep navy — header / dark-surface actions. */
	Darkblue: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.darkblue)),
	/** Medium slate — inactive status pills or subdued actions. */
	DarkLight: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.darkLight)),
	/** Light gray fill with dark text — inactive or toggle-off states. */
	Light: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.light)),
	/** White fill with light border — neutral secondary on form footers. */
	Neutral: ButtonWrapper(createButtonStyles(BUTTON_PALETTES.neutral)),
};

/** Contained trigger styles aligned with branded MUI buttons (dropdowns, pills). */
export function getContainedButtonStyle(paletteKey = 'secondary') {
	const palette = BUTTON_PALETTES[paletteKey] || BUTTON_PALETTES.secondary
	return {
		color: palette.textOnMain,
		backgroundColor: palette.containedBg,
		borderColor: palette.containedBg,
		hoverBackground: palette.containedHover,
	}
}
